import { useEffect, useState, type FC } from 'react';

import { useDebounce } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';

import { Box, Icon, Switch, Tooltip, Typography } from '@myn/mui';

import {
  useCampaignCurrencyMode,
  useSetCampaignCurrencyMode,
} from '../../../hooks/useCampaignCurrencyMode.js';

type Props = {
  size?: 'small' | 'large';
  isDisabled?: boolean;
};

const DEBOUNCE_DELAY = 100;
const CampaignCurrencySwitch: FC<Props> = ({
  size = 'large',
  isDisabled = false,
}) => {
  const { t } = useTranslation('@myn/planning');

  const [isCampaignCurrencyMode] = useCampaignCurrencyMode();
  const setCampaignCurrencyMode = useSetCampaignCurrencyMode();
  const [isChecked, setIsChecked] = useState(isCampaignCurrencyMode);
  const isCheckedDebounced = useDebounce(isChecked, DEBOUNCE_DELAY);

  const tooltipTitle = t(
    'Activate to view the entire buying plan in the currency of the campaign',
  );

  const isLargeSize = size === 'large';

  const handleToggle = () => {
    setIsChecked((prev) => !prev);
  };

  useEffect(() => {
    if (isCheckedDebounced !== isCampaignCurrencyMode) {
      setCampaignCurrencyMode(isCheckedDebounced);
    }
  }, [isCampaignCurrencyMode, isCheckedDebounced]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        columnGap: 1,
        color: isDisabled ? 'text.disabled02' : 'auto',
      }}
    >
      <Switch
        aria-label="Campaign currency toggle"
        size="small"
        checked={isChecked}
        onClick={handleToggle}
        disabled={isDisabled}
      />
      {isLargeSize ? (
        <Typography variant="bodyShort01" whiteSpace="nowrap">
          {t('Campaign currency')}
        </Typography>
      ) : (
        <Icon component="Money" />
      )}
      <Tooltip
        title={tooltipTitle}
        placement="top"
        disableHoverListener={isDisabled}
      >
        <Box display="flex">
          <Icon component="Information" />
        </Box>
      </Tooltip>
    </Box>
  );
};

export default CampaignCurrencySwitch;
